import React, { useState, useEffect, useRef } from 'react';

/*
  CareerCoPilot — career benchmark and skill gap page.
  Props: navigateTo, goBack
*/
const CareerCoPilot = ({ navigateTo, goBack }) => {
  const [careerProfile, setCareerProfile] = useState(null);
  const [careerLoading, setCareerLoading] = useState(true);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [benchmarkForm, setBenchmarkForm] = useState({ role: '', city: '', salary: '', experience: '', industry: '' });
  const [skillGaps, setSkillGaps] = useState(null);
  const [skillGapLoading, setSkillGapLoading] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [resumeUploadMessage, setResumeUploadMessage] = useState('');
  const resumeInputRef = useRef(null);

  useEffect(() => {
    fetch('/api/finance?action=career-profile', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.profile) {
          const p = data.profile;
          setCareerProfile(p);
          setBenchmarkForm({
            role: p.currentRole || '',
            city: p.city || '',
            salary: '',
            experience: p.yearsOfExperience != null ? String(p.yearsOfExperience) : '',
            industry: p.industry || '',
          });
          if (p.salaryBenchmark) setBenchmarkData(p.salaryBenchmark);
        }
      })
      .catch(() => {})
      .finally(() => setCareerLoading(false));
  }, []);

  const handleResumeUpload = async (file) => {
    if (!file) return;
    setUploadingResume(true);
    setResumeUploadMessage('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch('/api/finance?action=upload-resume', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      const p = data.profile;
      setCareerProfile(p);
      setBenchmarkForm({
        role: p.currentRole || '',
        city: p.city || '',
        salary: '',
        experience: p.yearsOfExperience != null ? String(p.yearsOfExperience) : '',
        industry: p.industry || '',
      });
      setResumeUploadMessage(`Found: ${p.currentRole || 'role unknown'} at ${p.currentEmployer || 'employer unknown'}, ${p.yearsOfExperience ?? '?'} years experience`);
    } catch (err) {
      setResumeUploadMessage(err.message);
    } finally {
      setUploadingResume(false);
    }
  };

  const handleGetBenchmark = async (e) => {
    e.preventDefault();
    setBenchmarkLoading(true);
    try {
      const res = await fetch('/api/finance?action=salary-benchmark', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentRole: benchmarkForm.role,
          city: benchmarkForm.city,
          currentSalary: Number(benchmarkForm.salary),
          yearsOfExperience: Number(benchmarkForm.experience),
          industry: benchmarkForm.industry,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Benchmark failed');
      setBenchmarkData(data.benchmark);
    } catch (err) {
      setResumeUploadMessage(err.message);
    } finally {
      setBenchmarkLoading(false);
    }
  };

  const handleGetSkillGaps = async () => {
    setSkillGapLoading(true);
    try {
      const res = await fetch('/api/finance?action=skill-gap', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentRole: careerProfile.currentRole,
          skills: careerProfile.skills || [],
          careerLevel: careerProfile.careerLevel,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Skill gap analysis failed');
      setSkillGaps(data.gaps);
    } catch (err) {
      setResumeUploadMessage(err.message);
    } finally {
      setSkillGapLoading(false);
    }
  };

  const fmt = n => `Rs.${Number(n).toLocaleString('en-IN')}`;

  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: '8px',
    border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)',
    fontSize: '14px', outline: 'none', transition: 'border-color 0.2s',
    boxSizing: 'border-box', fontFamily: 'inherit',
  };

  const assessmentCfg = benchmarkData ? {
    underpaid:    { color: '#F59E0B', bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.25)',  label: `Earning ${fmt(Math.abs(benchmarkData.gap))} below market` },
    'market-rate':{ color: '#10B981', bg: 'rgba(16,185,129,0.08)',  border: 'rgba(16,185,129,0.25)',  label: 'At market rate for your role' },
    'above-market':{ color: 'var(--accent)', bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.25)', label: 'Earning above market rate' },
  }[benchmarkData.assessment] || {} : {};

  return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '48px 24px 80px' }}>

        {/* Back */}
        <button
          onClick={() => goBack('personal-dashboard')}
          style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: 0, marginBottom: '48px', fontFamily: 'inherit', transition: 'color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
        >← Back</button>

        {/* Heading */}
        <div style={{ marginBottom: '40px' }}>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>Career</p>
          <h1 style={{ fontSize: '28px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.02em', margin: '0 0 8px' }}>Career Co-Pilot</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>Benchmark your salary, close skill gaps, and grow faster.</p>
        </div>

        {/* Loading */}
        {careerLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} className="axigon-skeleton" style={{ height: '80px', borderRadius: '10px' }} />
            ))}
          </div>
        )}

        {/* ── SECTION 1: Resume Upload ── */}
        {!careerLoading && !careerProfile && (
          <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '14px', padding: '36px', textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>Upload your resume or CV</h2>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 28px', lineHeight: 1.6 }}>We'll extract your role, experience, and skills automatically.<br />PDF only.</p>

            <input
              ref={resumeInputRef}
              type="file"
              accept=".pdf"
              style={{ display: 'none' }}
              onChange={e => handleResumeUpload(e.target.files?.[0])}
            />
            <button
              onClick={() => resumeInputRef.current?.click()}
              disabled={uploadingResume}
              style={{ padding: '12px 28px', borderRadius: '9px', background: 'var(--accent)', color: 'white', border: 'none', fontSize: '14px', fontWeight: 700, cursor: uploadingResume ? 'default' : 'pointer', opacity: uploadingResume ? 0.7 : 1, fontFamily: 'inherit', boxShadow: '0 0 24px rgba(139,92,246,0.35)', transition: 'all 0.2s' }}
            >{uploadingResume ? 'Uploading...' : 'Upload Resume'}</button>

            {resumeUploadMessage && (
              <p style={{ marginTop: '16px', fontSize: '13px', color: resumeUploadMessage.startsWith('Found') ? '#10B981' : '#F43F5E', lineHeight: 1.6 }}>{resumeUploadMessage}</p>
            )}
          </div>
        )}

        {/* ── AFTER UPLOAD: Profile summary + Sections 2 & 3 ── */}
        {!careerLoading && careerProfile && (
          <>
            {/* Parsed profile summary */}
            <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 24px', marginBottom: '28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' }}>{careerProfile.currentRole || 'Role unknown'}</p>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
                  {careerProfile.currentEmployer || 'Employer unknown'}
                  {careerProfile.yearsOfExperience != null ? ` · ${careerProfile.yearsOfExperience} yrs` : ''}
                  {careerProfile.city ? ` · ${careerProfile.city}` : ''}
                </p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(careerProfile.skills || []).slice(0, 5).map(s => (
                  <span key={s} style={{ fontSize: '11px', fontWeight: 600, color: 'var(--accent)', backgroundColor: 'rgba(0,194,124,0.08)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '6px', padding: '3px 9px' }}>{s}</span>
                ))}
              </div>
            </div>

            {resumeUploadMessage && !resumeUploadMessage.startsWith('Found') && (
              <p style={{ fontSize: '13px', color: '#F43F5E', marginBottom: '16px' }}>{resumeUploadMessage}</p>
            )}

            {/* ── SECTION 2: Salary Benchmark ── */}
            <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '14px', padding: '28px', marginBottom: '20px' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 16px' }}>Salary Benchmark</p>

              {/* No benchmark yet — show form */}
              {!benchmarkData && (
                <form onSubmit={handleGetBenchmark}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Role</label>
                      <input type="text" value={benchmarkForm.role} onChange={e => setBenchmarkForm(p => ({ ...p, role: e.target.value }))} style={inputStyle} required
                        onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'} onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>City</label>
                      <input type="text" value={benchmarkForm.city} onChange={e => setBenchmarkForm(p => ({ ...p, city: e.target.value }))} style={inputStyle} required
                        onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'} onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Current Salary (Rs. annual)</label>
                      <input type="number" value={benchmarkForm.salary} onChange={e => setBenchmarkForm(p => ({ ...p, salary: e.target.value }))} style={inputStyle} placeholder="e.g. 1200000" required min="0"
                        onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'} onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Years of Experience</label>
                      <input type="number" value={benchmarkForm.experience} onChange={e => setBenchmarkForm(p => ({ ...p, experience: e.target.value }))} style={inputStyle} placeholder="e.g. 5" required min="0"
                        onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'} onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={benchmarkLoading}
                    style={{ padding: '11px 24px', borderRadius: '8px', background: 'var(--accent)', color: 'white', border: 'none', fontSize: '14px', fontWeight: 700, cursor: benchmarkLoading ? 'default' : 'pointer', opacity: benchmarkLoading ? 0.7 : 1, fontFamily: 'inherit', transition: 'opacity 0.2s' }}
                  >{benchmarkLoading ? 'Analysing...' : 'Get salary benchmark'}</button>
                </form>
              )}

              {/* Benchmark results */}
              {benchmarkData && (
                <>
                  {/* Market range — three stat cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                    {[
                      { label: 'Market Min', value: fmt(benchmarkData.marketMin), sub: '10th percentile' },
                      { label: 'Market Median', value: fmt(benchmarkData.marketMedian), sub: '50th percentile', highlight: true },
                      { label: 'Market Max', value: fmt(benchmarkData.marketMax), sub: '90th percentile' },
                    ].map(({ label, value, sub, highlight }) => (
                      <div key={label} style={{ backgroundColor: highlight ? 'rgba(0,194,124,0.06)' : 'var(--bg-secondary)', border: `1px solid ${highlight ? 'rgba(139,92,246,0.25)' : 'var(--border)'}`, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                        <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px', fontWeight: 600 }}>{label}</p>
                        <p style={{ fontSize: '16px', fontWeight: 700, color: highlight ? '#A78BFA' : 'var(--text-primary)', margin: '0 0 4px' }}>{value}</p>
                        <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: 0 }}>{sub}</p>
                      </div>
                    ))}
                  </div>

                  {/* Assessment banner */}
                  <div style={{ backgroundColor: assessmentCfg.bg, border: `1px solid ${assessmentCfg.border}`, borderRadius: '8px', padding: '14px 18px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                    <p style={{ fontSize: '14px', fontWeight: 700, color: assessmentCfg.color, margin: 0 }}>{assessmentCfg.label}</p>
                    <span style={{ fontSize: '12px', color: assessmentCfg.color, fontWeight: 600, opacity: 0.8 }}>{benchmarkData.userPercentile}th percentile</span>
                  </div>

                  {/* Negotiation tip */}
                  <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px 18px', marginBottom: '16px' }}>
                    <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>Negotiation Tip</p>
                    <p style={{ fontSize: '13px', color: 'var(--text-primary)', margin: 0, lineHeight: 1.65 }}>{benchmarkData.negotiationTip}</p>
                  </div>

                  {/* Top paying companies */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 600 }}>Top paying:</span>
                    {(benchmarkData.topPayingCompanies || []).map(c => (
                      <span key={c} style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', backgroundColor: '#1a1a20', border: '1px solid #2a2a35', borderRadius: '6px', padding: '3px 10px' }}>{c}</span>
                    ))}
                  </div>

                  {/* Re-run link */}
                  <button
                    onClick={() => setBenchmarkData(null)}
                    style={{ marginTop: '20px', background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: 'inherit', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
                  >Re-run with different inputs →</button>
                </>
              )}
            </div>

            {/* ── SECTION 3: Skill Gaps ── */}
            <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '14px', padding: '28px' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 16px' }}>Skill Gaps</p>

              {!skillGaps && (
                <div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 20px', lineHeight: 1.6 }}>
                    Find out which 3 skills will move the needle most for your career progression.
                  </p>
                  <button
                    onClick={handleGetSkillGaps}
                    disabled={skillGapLoading}
                    style={{ padding: '11px 24px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.18)', fontSize: '14px', fontWeight: 700, cursor: skillGapLoading ? 'default' : 'pointer', opacity: skillGapLoading ? 0.7 : 1, fontFamily: 'inherit', transition: 'all 0.2s' }}
                    onMouseEnter={e => { if (!skillGapLoading) { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; }}}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; }}
                  >{skillGapLoading ? 'Analysing...' : 'Show my skill gaps'}</button>
                </div>
              )}

              {skillGaps && (
                <>
                  {/* Gap cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                    {(skillGaps.gaps || []).map((gap, i) => {
                      const isHigh = gap.priority === 'high';
                      return (
                        <div key={i} style={{ borderLeft: `3px solid ${isHigh ? '#F59E0B' : 'var(--accent)'}`, backgroundColor: 'var(--bg-secondary)', borderRadius: '0 8px 8px 0', padding: '16px 18px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{gap.skill}</span>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: isHigh ? '#F59E0B' : '#818CF8', backgroundColor: isHigh ? 'rgba(245,158,11,0.1)' : 'rgba(0,194,124,0.08)', border: `1px solid ${isHigh ? 'rgba(245,158,11,0.3)' : 'rgba(99,102,241,0.3)'}`, borderRadius: '4px', padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{gap.priority}</span>
                          </div>
                          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 6px', lineHeight: 1.55 }}>{gap.reason}</p>
                          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.55 }}>
                            <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>How: </span>{gap.learningPath}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Time + advice */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: '16px', borderTop: '1px solid var(--bg-secondary)' }}>
                      <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Time to next level</span>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{skillGaps.timeToNextLevel}</span>
                    </div>
                    <div style={{ backgroundColor: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '8px', padding: '14px 16px' }}>
                      <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>Key Advice</p>
                      <p style={{ fontSize: '13px', color: 'var(--text-primary)', margin: 0, lineHeight: 1.65 }}>{skillGaps.keyAdvice}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => setSkillGaps(null)}
                    style={{ marginTop: '20px', background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: 'inherit', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
                  >Re-run analysis →</button>
                </>
              )}
            </div>
          </>
        )}

      </div>
    </section>
  );
};

export default CareerCoPilot;
